import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ClipboardEvent, KeyboardEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faImage, faXmark } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError } from '@/utils/toasts';
import type { DayTradingSession } from '@/models/day-trading-session';
import type { DayTradingSessionID } from '@/models/ids';

interface ChatInputProps {
  sessionId: DayTradingSessionID;
  onSessionUpdate: (session: DayTradingSession) => void;
  disabled?: boolean;
}

export interface ChatInputHandle {
  setImage: (file: File) => void;
  isBusy: () => boolean;
}

function ChatInputInner(
  { sessionId, onSessionUpdate, disabled = false }: ChatInputProps,
  ref: React.Ref<ChatInputHandle>,
) {
  const [text, setText] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow textarea up to 4 lines.
  useEffect(() => {
    const el = textareaRef.current;
    if (el === null) return;
    // Reset first so shrinking works when content is deleted.
    el.style.height = 'auto';
    const styles = window.getComputedStyle(el);
    const lineHeight = parseFloat(styles.lineHeight) || 20;
    const paddingY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    const borderY = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
    const maxHeight = lineHeight * 4 + paddingY + borderY;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [text]);

  const clearImage = useCallback(() => {
    setImage(null);
    if (imagePreview !== null) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    if (fileInputRef.current !== null) fileInputRef.current.value = '';
  }, [imagePreview]);

  const handleSelectImage = useCallback((file: File) => {
    if (imagePreview !== null) URL.revokeObjectURL(imagePreview);
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  }, [imagePreview]);

  useImperativeHandle(ref, () => ({
    setImage: (file: File) => handleSelectImage(file),
    isBusy: () => sending || disabled,
  }), [handleSelectImage, sending, disabled]);

  const handlePaste = useCallback((e: ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (items === undefined) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file !== null) {
          e.preventDefault();
          handleSelectImage(file);
          return;
        }
      }
    }
  }, [handleSelectImage]);

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (trimmed === '' && image === null) return;
    if (sending) return;

    setSending(true);
    try {
      const session = await APIRoutes.sendDayTradingChatMessage(sessionId, {
        text: trimmed !== '' ? trimmed : undefined,
        image: image ?? undefined,
      });
      onSessionUpdate(session);
      setText('');
      clearImage();
    } catch (err) {
      handleAPIError(err, 'Fehler beim Senden der Nachricht');
    } finally {
      setSending(false);
    }
  }, [text, image, sending, sessionId, onSessionUpdate, clearImage]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <div className="relative border-t border-gray-100 dark:border-gray-700 p-2 bg-white dark:bg-gray-800">
      {imagePreview !== null && (
        <div className="mb-2 relative inline-block">
          <img
            src={imagePreview}
            alt="Vorschau"
            className="max-h-32 rounded-lg border border-gray-200 dark:border-gray-700"
          />
          <button
            onClick={clearImage}
            disabled={sending}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center shadow cursor-pointer hover:bg-red-700 disabled:opacity-50"
            title="Bild entfernen"
          >
            <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
          </button>
        </div>
      )}

      <div className="flex items-end gap-2">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || sending}
          className="shrink-0 w-9 h-9 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          title="Bild anhängen"
        >
          <FontAwesomeIcon icon={faImage} className="w-4 h-4" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file !== undefined) handleSelectImage(file);
          }}
        />

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={disabled || sending}
          placeholder="Nachricht…"
          rows={1}
          className="flex-1 resize-none px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 leading-5"
        />

        <button
          onClick={handleSend}
          disabled={disabled || sending || (text.trim() === '' && image === null)}
          className="shrink-0 w-9 h-9 rounded-lg bg-blue-600 text-white hover:bg-blue-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          title="Senden"
        >
          <FontAwesomeIcon icon={faPaperPlane} className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(ChatInputInner);
export default ChatInput;
